import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Expand,
  Images,
  X,
  ArrowLeft,
} from "lucide-react";
import { getEntryBySectionSlug } from "../lib/entriesApi";



export default function EntryDetailPage() {
  const { sectionSlug, slug } = useParams();

  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [shownCount, setShownCount] = useState(20);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);

      try {
        const data = await getEntryBySectionSlug(sectionSlug, slug);

        if (!alive) return;

        setEntry(data || null);
        setActiveIndex(0);
        setShownCount(20);
      } catch (e) {
        console.error("Errore caricando dettaglio:", e);
        if (!alive) return;
        setEntry(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [sectionSlug, slug]);

  const gallery = useMemo(() => {
    if (!entry) return [];
    const arr = [];
    if (entry.coverUrl) arr.push(entry.coverUrl);
    if (Array.isArray(entry.images)) {
      entry.images.forEach((url) => {
        if (url && !arr.includes(url)) arr.push(url);
      });
    }
    return arr;
  }, [entry]);

  const totalImages = gallery.length;
  const shownImages = gallery.slice(0, shownCount);
  const canShowMore = totalImages > shownCount;

  const goPrev = useCallback(() => {
    if (!gallery.length) return;
    setActiveIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
  }, [gallery.length]);

  const goNext = useCallback(() => {
    if (!gallery.length) return;
    setActiveIndex((prev) => (prev + 1) % gallery.length);
  }, [gallery.length]);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    document.body.style.overflow = "hidden";
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    document.body.style.overflow = "";
  }, []);

  const lightboxPrev = useCallback(() => {
    if (!gallery.length) return;
    setLightboxIndex((prev) => ((prev ?? 0) - 1 + gallery.length) % gallery.length);
  }, [gallery.length]);

  const lightboxNext = useCallback(() => {
    if (!gallery.length) return;
    setLightboxIndex((prev) => ((prev ?? 0) + 1) % gallery.length);
  }, [gallery.length]);

  useEffect(() => {
    if (gallery.length <= 1) return;
    if (lightboxIndex !== null) return;

    const id = setInterval(() => {
      goNext();
    }, 4500);

    return () => clearInterval(id);
  }, [gallery.length, lightboxIndex, goNext]);

  useEffect(() => {
    const onKey = (e) => {
      if (lightboxIndex !== null) {
        if (e.key === "Escape") closeLightbox();
        if (e.key === "ArrowLeft") lightboxPrev();
        if (e.key === "ArrowRight") lightboxNext();
        return;
      }

      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIndex, goPrev, goNext, closeLightbox, lightboxPrev, lightboxNext]);

  if (loading) {
    return (
      <main className="mx-auto w-[min(1400px,94%)] py-10 sm:py-12">
        <div className="rounded-[32px] border border-slate-200/80 bg-white/80 p-8 shadow-[0_18px_60px_rgba(15,23,42,.07)] backdrop-blur-xl">
          <div className="text-sm font-bold text-slate-500">Caricamento contenuto...</div>
        </div>
      </main>
    );
  }

  if (!entry) {
    return (
      <main className="mx-auto w-[min(1400px,94%)] py-10 sm:py-12">
        <div className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-[0_18px_60px_rgba(15,23,42,.05)]">
          <h1 className="text-3xl font-black text-slate-900">Contenuto non trovato</h1>
          <p className="mt-2 text-sm font-semibold text-slate-500">
            La card richiesta non esiste oppure non è disponibile.
          </p>
        </div>
      </main>
    );
  }

  return (
    <>
<main className="mx-auto w-[min(1280px,94%)] pt-28 pb-10 sm:pt-32 sm:pb-12">        <div className="mb-6 flex items-center justify-between gap-3">
          <NavLink
            to={`/${sectionSlug}`}
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-black text-white shadow-[0_18px_60px_rgba(2,6,23,.16)] transition hover:-translate-y-[1px] hover:bg-black"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna alla sezione
          </NavLink>

          <div className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#CC3A21]/15 bg-white/90 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-slate-500 shadow-sm backdrop-blur-md">
            <Images className="h-4 w-4 text-[#7A2417]" />
            {totalImages} immagini
          </div>
        </div>

        <section className="relative overflow-hidden rounded-[36px] border border-slate-200/80 bg-white/78 shadow-[0_25px_100px_rgba(15,23,42,.08)] backdrop-blur-xl">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(900px_400px_at_0%_0%,rgba(204,58,33,.10),transparent_60%),radial-gradient(800px_380px_at_100%_100%,rgba(251,191,36,.07),transparent_58%),linear-gradient(180deg,rgba(255,255,255,.70),rgba(248,250,252,.92))]" />
          </div>

          <div className="relative p-5 sm:p-8 lg:p-10">
            <div className="mx-auto max-w-5xl text-center">
              

              <h1 className="mx-auto mt-5 max-w-4xl text-3xl font-black tracking-tight text-slate-900 sm:text-4xl lg:text-5xl xl:text-6xl">
                {entry.titolo || entry.slug}
              </h1>

              {entry.descrizione ? (
                <p className="mx-auto mt-5 max-w-3xl text-sm font-medium leading-7 text-slate-600 sm:text-base lg:text-lg lg:leading-8">
                  {entry.descrizione}
                </p>
              ) : null}
            </div>

            {gallery.length > 0 ? (
              <>
                <div className="mx-auto mt-8 max-w-5xl sm:mt-10">
                  <div className="relative overflow-hidden rounded-[30px] border border-slate-200/80 bg-white/70 shadow-[0_20px_80px_rgba(15,23,42,.10)] backdrop-blur-xl">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,.78),transparent_42%)]" />
                    <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />

                    <div className="relative px-3 py-3 sm:px-4 sm:py-4">
                      <div className="relative h-[300px] rounded-[24px] bg-slate-100 sm:h-[380px] lg:h-[460px]">
                        <img
                          src={gallery[activeIndex]}
                          alt={`${entry.titolo || entry.slug} ${activeIndex + 1}`}
                          className="absolute inset-0 h-full w-full scale-105 object-cover blur-2xl opacity-30"
                          aria-hidden="true"
                        />

                        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-slate-900/5" />

                        <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-[24px]">
                          <img
                            src={gallery[activeIndex]}
                            alt={`${entry.titolo || entry.slug} ${activeIndex + 1}`}
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>

                        {gallery.length > 1 && (
                          <>
                            <button
                              onClick={goPrev}
                              className="absolute left-3 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-2xl border border-slate-200 bg-white/92 text-slate-900 shadow-xl backdrop-blur-md transition hover:scale-[1.03] hover:bg-white sm:left-4 sm:h-12 sm:w-12"
                              aria-label="Immagine precedente"
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </button>

                            <button
                              onClick={goNext}
                              className="absolute right-3 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-2xl border border-slate-200 bg-white/92 text-slate-900 shadow-xl backdrop-blur-md transition hover:scale-[1.03] hover:bg-white sm:right-4 sm:h-12 sm:w-12"
                              aria-label="Immagine successiva"
                            >
                              <ChevronRight className="h-5 w-5" />
                            </button>
                          </>
                        )}

                        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3 sm:bottom-4 sm:left-4 sm:right-4">
                          <div className="rounded-2xl border border-white/15 bg-black/45 px-4 py-2 text-[11px] font-black uppercase tracking-[0.16em] text-white backdrop-blur-md sm:text-xs">
                            {activeIndex + 1} / {gallery.length}
                          </div>

                          <button
                            onClick={() => openLightbox(activeIndex)}
                            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/92 px-4 py-3 text-sm font-black text-slate-900 shadow-xl backdrop-blur-md transition hover:-translate-y-[1px] hover:bg-white"
                          >
                            <Expand className="h-4 w-4" />
                            Apri
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mx-auto mt-12 max-w-6xl">
                  <div className="mb-5 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-black uppercase tracking-[0.16em] text-slate-500">
                        Gallery completa
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-600">
                        Tutte le immagini del contenuto
                      </div>
                    </div>

                    <div className="rounded-full border border-[#CC3A21]/15 bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-slate-500 shadow-sm">
                      {shownImages.length} / {gallery.length}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                    {shownImages.map((url, index) => (
                      <button
                        key={`${url}-${index}`}
                        onClick={() => openLightbox(index)}
                        className="group relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white/80 shadow-[0_18px_60px_rgba(2,6,23,.06)] transition hover:-translate-y-[2px] hover:shadow-[0_22px_70px_rgba(2,6,23,.10)]"
                      >
                        <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)] opacity-0 transition group-hover:opacity-100" />

                        <div className="relative h-40 bg-slate-100 sm:h-48">
                          <img
                            src={url}
                            alt={`${entry.titolo || entry.slug} gallery ${index + 1}`}
                            className="absolute inset-0 h-full w-full scale-105 object-cover blur-xl opacity-25"
                            loading="lazy"
                            aria-hidden="true"
                          />
                          <div className="relative flex h-full w-full items-center justify-center p-2">
                            <img
                              src={url}
                              alt={`${entry.titolo || entry.slug} gallery ${index + 1}`}
                              className="max-h-full max-w-full object-contain"
                              loading="lazy"
                            />
                          </div>
                        </div>

                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/35 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />

                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between opacity-0 transition group-hover:opacity-100">
                          <span className="rounded-full bg-white/92 px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-slate-900">
                            {index + 1}
                          </span>
                          <span className="grid h-9 w-9 place-items-center rounded-full bg-white/92 text-slate-900 shadow-lg">
                            <Expand className="h-4 w-4" />
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {canShowMore && (
                    <div className="mt-6 flex justify-center">
                      <button
                        onClick={() =>
                          setShownCount((prev) => Math.min(prev + 20, gallery.length))
                        }
                        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-black text-white shadow-[0_18px_60px_rgba(2,6,23,.16)] transition hover:-translate-y-[1px] hover:bg-black"
                      >
                        <Images className="h-4 w-4" />
                        Mostra altre ({Math.min(20, gallery.length - shownCount)})
                      </button>
                    </div>
                  )}

                  {gallery.length > 20 && shownCount > 20 && (
                    <div className="mt-3 flex justify-center">
                      <button
                        onClick={() => setShownCount(20)}
                        className="text-xs font-black uppercase tracking-[0.14em] text-slate-500 transition hover:text-slate-900"
                      >
                        Mostra solo le prime 20
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="mx-auto mt-8 max-w-4xl rounded-[28px] border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center">
                <div className="text-lg font-black text-slate-900">Nessuna immagine presente</div>
                <div className="mt-2 text-sm font-semibold text-slate-500">
                  Questo contenuto non ha ancora una galleria disponibile.
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      {lightboxIndex !== null && gallery[lightboxIndex] && (
        <div className="fixed inset-0 z-[9999] bg-slate-950/92 backdrop-blur-md">
          <button
            onClick={closeLightbox}
            className="absolute right-4 top-4 z-20 grid h-12 w-12 place-items-center rounded-2xl bg-white/92 text-slate-900 shadow-xl transition hover:scale-[1.03] sm:right-6 sm:top-6"
            aria-label="Chiudi lightbox"
          >
            <X className="h-5 w-5" />
          </button>

          {gallery.length > 1 && (
            <>
              <button
                onClick={lightboxPrev}
                className="absolute left-3 top-1/2 z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-2xl bg-white/92 text-slate-900 shadow-xl transition hover:scale-[1.03] sm:left-6 sm:h-14 sm:w-14"
                aria-label="Immagine precedente"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>

              <button
                onClick={lightboxNext}
                className="absolute right-3 top-1/2 z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-2xl bg-white/92 text-slate-900 shadow-xl transition hover:scale-[1.03] sm:right-6 sm:h-14 sm:w-14"
                aria-label="Immagine successiva"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          <div className="absolute left-1/2 top-5 z-20 -translate-x-1/2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-white ring-1 ring-white/15 backdrop-blur-md sm:top-6 sm:text-sm">
            {lightboxIndex + 1} / {gallery.length}
          </div>

          <div className="flex h-full w-full items-center justify-center p-4 sm:p-8">
            <img
              src={gallery[lightboxIndex]}
              alt={`${entry.titolo || entry.slug} ${lightboxIndex + 1}`}
              className="max-h-full max-w-full rounded-[28px] object-contain shadow-[0_30px_120px_rgba(0,0,0,.45)]"
            />
          </div>
        </div>
      )}
    </>
  );
}